"use client";

import { useEffect, useState } from "react";
import Layout from "../../components/app/Layout";
import BaseContainer from "../../components/form/v2/BaseContainer";
import { Loading } from "../../components/shared/Loading";
import { IntegrationItem } from "../../models/integration-item.model";
import { integrationService } from "../../service/integration.service";
import { formatDate, getFirstDayOfMonth } from "../../utils/date.util";
import { Card } from "../../third-party/shadcn/card";
import { RootState, store } from "../../store";
import {
  setActiveIntegrations,
  setIntegrationItems,
} from "../../store/slice/Workflows/workflow.slice";
import { useSelector } from "react-redux";
import { ReloadIcon } from "@radix-ui/react-icons";
import { errorToast } from "../../constants/toast.constants";
import { useToast } from "@chakra-ui/react";
import { Calendar1Icon } from "lucide-react";
import { MetricOverviewRow } from "../../components/dashboard/card/MetricOverviewRow";
import { RunFrequency } from "../../components/dashboard/charts/v2/RunFrequency";
import { IntegrationPieChart } from "../../components/dashboard/charts/v2/IntegrationFailureRate";
import { RunTime } from "../../components/dashboard/charts/v2/RunTimes";
import { RunTimePieChart } from "../../components/dashboard/charts/v2/RunTimePieChart";

export const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [monthRuns, setMonthRuns] = useState<IntegrationItem[]>([]);
  const [averageTime, setAverageTime] = useState(0);
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([]);

  const toast = useToast();

  const integrationWorkflows = useSelector(
    (state: RootState) => state.workflows
  ) ?? { integrationItems: [], activeIntegrations: [] };

  const init = async () => {
    await setIntegrationData();
  };

  const setIntegrationData = async (force: boolean = false) => {
    let runs = integrationWorkflows.integrationItems;
    let activeIntegrations = integrationWorkflows.activeIntegrations;

    if (shouldLoadNewData() || force) {
      setLoading(true);

      try {
        runs = await integrationService.getWorkflowsWithRuns();
        activeIntegrations = Object.values(
          runs.reduce(
            (acc, current) => ({
              ...acc,
              [current.name]: acc[current.name] || current,
            }),
            {} as Record<string, IntegrationItem>
          )
        );

        store.dispatch(setIntegrationItems(runs));
        store.dispatch(setActiveIntegrations(activeIntegrations));
      } catch (error) {
        toast(errorToast("Failed to fetch integrations."));
      } finally {
        setLoading(false);
      }
    }

    const dateFiltered = runs.filter(
      (run) => new Date(run.lastRunDate) >= new Date(getFirstDayOfMonth())
    );

    const avgTime =
      dateFiltered.reduce((acc, cur) => acc + cur.duration, 0) /
      dateFiltered.length;

    setAverageTime(+avgTime.toFixed(2));
    setMonthRuns(dateFiltered);

    const { count } = await integrationService.getWorkflowCount();
    setCount(count);

    setIntegrations(activeIntegrations);
  };

  const shouldLoadNewData = () => {
    let runs = integrationWorkflows.integrationItems;
    let activeIntegrations = integrationWorkflows.activeIntegrations;

    if (!runs?.length || !activeIntegrations?.length) return true;

    const lastLoadDate = new Date(integrationWorkflows.lastLoadDate);
    const now = new Date();
    const diffInMs = now.getTime() - lastLoadDate.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays >= 1 || lastLoadDate < new Date(getFirstDayOfMonth());
  };

  const lastRefreshedDate = integrationWorkflows.lastLoadDate;
  const formattedLastRefreshedDate = formatDate(lastRefreshedDate);

  useEffect(() => {
    init();
  }, []);

  return (
    <Layout>
      <Loading
        loading={loading}
        text="Fetching all your data - this may take a few moments."
      />

      <BaseContainer className="flex-col flex-grow rounded-sm min-h-[calc(100vh-104px)] px-6 pb-[36px] border-0 bg-transparent shadow-none">
        <div className="my-6 animate-fade-in">
          <div className="label-caps mb-2">Operations</div>
          <div className="text-3xl font-medium tracking-tight text-foreground">
            Overview
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <DashboardHeader
            formattedLastRefreshedDate={formattedLastRefreshedDate}
            setIntegrationData={setIntegrationData}
          />

          <MetricOverviewRow
            count={count}
            averageTime={averageTime}
            monthRuns={monthRuns}
          />

          <div className="flex gap-3">
            <Card className="w-3/4 h-[50vh] max-h-[500px]">
              <RunFrequency data={monthRuns} />
            </Card>

            <Card className="w-1/4 h-[50vh] max-h-[500px]">
              <IntegrationPieChart data={monthRuns} />
            </Card>
          </div>

          <div className="flex gap-3 h-[60vh]">
            <Card className="w-3/4">
              <RunTime data={monthRuns} />
            </Card>

            <Card className="w-1/4">
              <RunTimePieChart data={monthRuns} />
            </Card>
          </div>
        </div>
      </BaseContainer>
    </Layout>
  );
};

const DashboardHeader = ({
  formattedLastRefreshedDate,
  setIntegrationData,
}: {
  formattedLastRefreshedDate: string;
  setIntegrationData: (force: boolean) => void;
}) => {
  return (
    <div className="flex flex-row items-center py-2 mb-1 justify-between border-b border-border">
      <div className="flex flex-row items-center gap-2.5 text-muted-foreground">
        <Calendar1Icon className="h-4 w-4 text-signal" />
        <div className="text-sm font-medium tracking-wide">
          01 {new Date().toLocaleString("default", { month: "long" })} — Today
        </div>
      </div>
      <div className="flex flex-row gap-4 items-center text-xs text-muted-foreground">
        <div className="flex flex-row gap-2 font-mono">
          <span className="uppercase tracking-[0.1em] text-[10px]">
            Last refreshed
          </span>
          <span className="text-foreground/80">{formattedLastRefreshedDate}</span>
        </div>
        <button
          type="button"
          className="h-8 px-3 flex justify-center items-center rounded-sm cursor-pointer gap-2 border border-border bg-card text-foreground text-xs font-medium uppercase tracking-[0.08em] hover:border-signal hover:text-signal transition-colors"
          onClick={() => setIntegrationData(true)}
        >
          <ReloadIcon className="h-3 w-3" />
          Refresh
        </button>
      </div>
    </div>
  );
};
